import { ApiServiceCredentials } from '@barbanet/api-service'
import { defineStore } from 'pinia'

import { computed, inject, ref } from 'vue'
import { VueCookies } from 'vue-cookies'
import { useRoute, useRouter } from 'vue-router'

import { useExceptionHandler } from '@barbanet/ui-kit/composables'

import { userMeService } from '../domains/user/services/user'
import { UserMeResponse } from '../domains/user/types/dto/user'
import { CREDENTIALS_COOKIE } from '../helpers/cookie'

export const useAccountStore = defineStore('account', () => {
	const $cookies = inject<VueCookies>('$cookies')
	const router = useRouter()
	const route = useRoute()

	// get account from cookies if exists to set as initial value
	let accountFromCookie = $cookies?.get(CREDENTIALS_COOKIE.key)
	if (accountFromCookie) {
		accountFromCookie = accountFromCookie as BarbanetCredentials
	}

	const credentials = ref<BarbanetCredentials | null>(accountFromCookie || null)
	const isLoggedIn = computed(() => !!credentials.value)

	const logout = () => {
		credentials.value = null
		$cookies?.remove(CREDENTIALS_COOKIE.key)

		router.replace({ name: 'AuthenticatePage', query: { to: route.fullPath } })
	}

	const setCredentials = (
		data: BarbanetCredentials | ApiServiceCredentials
	) => {
		const newCredentials = { ...(credentials.value || {}), ...data }

		if (newCredentials.userId) {
			credentials.value = newCredentials as BarbanetCredentials
			$cookies?.set(
				CREDENTIALS_COOKIE.key,
				newCredentials,
				CREDENTIALS_COOKIE.expires,
				CREDENTIALS_COOKIE.path,
				undefined,
				CREDENTIALS_COOKIE.secure,
				CREDENTIALS_COOKIE.sameSite
			)
		} else {
			logout()
		}
	}

	const userData = ref<UserMeResponse | null>(null)
	const userDataLoading = ref<boolean>(false)

	const getUserData = async (): Promise<UserMeResponse | null> => {
		if (!isLoggedIn.value) {
			return null
		}

		const data = userData.value
		const hasMissingData =
			data === null ||
			Object.keys(data).some((key) => data[key as keyof typeof data] === null)

		if (hasMissingData) {
			userDataLoading.value = true
			try {
				const response = await userMeService()

				if (response && response.data && response.data.content) {
					userData.value = response.data.content
					return userData.value
				}

				return null
			} catch (error) {
				useExceptionHandler(error)
				return null
			} finally {
				userDataLoading.value = false
			}
		} else {
			return userData.value
		}
	}

	return {
		credentials,
		isLoggedIn,
		userDataLoading,
		setCredentials,
		logout,
		getUserData
	}
})
